import React, { useContext, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ChartPieIcon,
  SparklesIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  UserPlusIcon,
  CheckIcon,
  EyeIcon,
} from "@heroicons/react/24/solid";
import { getPublicPortfolio } from "../api/usersApi";
import { sendFriendRequest } from "../api/communityApi";
import SEO from "../components/SEO";
import { AuthContext } from "../context/AuthContext";
import { formatCurrency } from "../utils/currency";

const ASSET_CLASS_COLOR = {
  equity: "#10b981",
  etf: "#34d399",
  mutual_fund: "#06b6d4",
  bond: "#3b82f6",
  crypto: "#f59e0b",
  cash: "#9ca3af",
  real_estate: "#a855f7",
  commodity: "#eab308",
  private: "#ec4899",
  collectible: "#fb7185",
  debt: "#ef4444",
  other: "#6b7280",
};

const Bar = ({ slice }) => {
  const color = ASSET_CLASS_COLOR[slice.assetClass] || "#6b7280";
  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span className="font-semibold text-gray-700 dark:text-gray-300">
          {slice.label}
        </span>
        <span className="text-gray-500">{slice.percentage.toFixed(1)}%</span>
      </div>
      <div className="h-2 rounded-full bg-gray-200 dark:bg-white/10 overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${slice.percentage}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
};

const PublicPortfolio = () => {
  const { slug } = useParams();
  const { isAuthenticated, user } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // Friend-request UX: 'idle' | 'sending' | 'sent' | 'self' | 'error'
  const [friendStatus, setFriendStatus] = useState("idle");
  const [friendMessage, setFriendMessage] = useState("");
  // Preview-as-visitor: when the page owner is logged in, let them
  // toggle into a mode that simulates how a stranger sees the page.
  const [previewMode, setPreviewMode] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await getPublicPortfolio(slug);
        if (alive) setData(res);
      } catch (err) {
        if (alive)
          setError(
            err?.response?.status === 404
              ? "This portfolio is private or doesn't exist."
              : "Could not load this portfolio.",
          );
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-black">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-black">
        <div className="max-w-md text-center px-6">
          <p className="text-3xl font-bold mb-2 text-gray-900 dark:text-white">404</p>
          <p className="text-gray-500 dark:text-gray-400 mb-6">
            {error || "This portfolio is private or doesn't exist."}
          </p>
          <Link
            to="/"
            className="inline-block px-5 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-semibold"
          >
            FinAI home
          </Link>
        </div>
      </div>
    );
  }

  const positive = (data.total_return_percent || 0) >= 0;
  const TrendIcon = positive ? ArrowTrendingUpIcon : ArrowTrendingDownIcon;

  const avatarSrc =
    data.avatar_url ||
    `https://ui-avatars.com/api/?name=${encodeURIComponent(
      data.display_name || "U",
    )}&background=4f46e5&color=fff&size=160`;

  // True when the logged-in viewer IS the page owner. Used to (a) hide the
  // friend-request button on their own page and (b) surface a "you're
  // viewing your own page" notice with a preview-mode toggle.
  const isOwner =
    isAuthenticated && user && data.user_id && user.id === data.user_id;
  // We treat the owner like a stranger when previewMode is on, so the
  // friend button etc render exactly as a visitor would see them.
  const isSelf = isOwner && !previewMode;

  const onAddFriend = async () => {
    if (!data.user_id) return;
    // Owner clicking the button in preview mode: don't actually fire the
    // request — they'd just hit a "can't friend yourself" 4xx. Show what
    // a successful click would look like.
    if (isOwner) {
      setFriendStatus("sent");
      setFriendMessage("(preview) Request would be sent");
      return;
    }
    setFriendStatus("sending");
    try {
      await sendFriendRequest(data.user_id);
      setFriendStatus("sent");
      setFriendMessage("Request sent");
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.error ||
        "Could not send request.";
      const lower = String(msg).toLowerCase();
      if (lower.includes("already")) {
        setFriendStatus("sent");
        setFriendMessage(msg);
      } else {
        setFriendStatus("error");
        setFriendMessage(msg);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:via-gray-950 dark:to-black text-gray-900 dark:text-white">
      {/* noindex: shared personal portfolios shouldn't land in search results,
          but OG/title still render a nice card when the link is shared. */}
      <SEO
        title={`${data.display_name || "Investor"}'s portfolio on FinAI`}
        bareTitle
        description={`See ${data.display_name || "this investor"}'s public portfolio on FinAI — holdings, allocation, and performance.`}
        image={avatarSrc}
        noindex
      />
      <div className="max-w-3xl mx-auto px-6 py-16">
        {/* OWNER NOTICE — only visible to the page owner */}
        {isOwner ? (
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3 p-3 rounded-xl bg-indigo-500/10 border border-indigo-500/30 text-sm">
            <div className="flex items-center gap-2 text-indigo-600 dark:text-indigo-400">
              <EyeIcon className="w-4 h-4" />
              <span className="font-semibold">
                {previewMode
                  ? "Previewing as a visitor — friend button is real but won't actually friend yourself."
                  : "This is your own public page. Strangers see exactly what's below."}
              </span>
            </div>
            <button
              onClick={() => setPreviewMode((v) => !v)}
              className="text-xs font-bold px-3 py-1 rounded-md bg-indigo-600 text-white hover:bg-indigo-700"
            >
              {previewMode ? "Exit preview" : "Preview as visitor"}
            </button>
          </div>
        ) : null}

        {/* HERO */}
        <div className="p-8 rounded-3xl bg-white/70 dark:bg-[#1a2332]/60 backdrop-blur-xl border border-white/40 dark:border-white/10 shadow-glass dark:shadow-glass-dark">
          <div className="flex flex-col sm:flex-row gap-6 items-start sm:items-center">
            <img
              src={avatarSrc}
              alt={data.display_name}
              className="w-24 h-24 rounded-2xl object-cover border-2 border-indigo-500/40"
            />
            <div className="flex-1 min-w-0">
              <h1 className="text-3xl font-bold truncate">{data.display_name}</h1>
              {data.share_bio ? (
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">
                  {data.share_bio}
                </p>
              ) : null}
              <div className="flex flex-wrap gap-2 mt-3">
                {data.country ? (
                  <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-sky-500/15 text-sky-500 border border-sky-500/30">
                    {data.country}
                  </span>
                ) : null}
                <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-500 border border-emerald-500/30">
                  {data.number_of_holdings} holdings
                </span>
              </div>
            </div>

            {/* Friend-request button — only when there's a logged-in
                visitor who isn't the page owner. */}
            {!isSelf && data.user_id ? (
              isAuthenticated ? (
                <button
                  onClick={onAddFriend}
                  disabled={friendStatus === "sending" || friendStatus === "sent"}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition shrink-0 ${
                    friendStatus === "sent"
                      ? "bg-emerald-500/15 text-emerald-500 border border-emerald-500/40 cursor-default"
                      : "bg-indigo-600 hover:bg-indigo-700 text-white"
                  }`}
                >
                  {friendStatus === "sent" ? (
                    <>
                      <CheckIcon className="w-4 h-4" />
                      {friendMessage || "Request sent"}
                    </>
                  ) : friendStatus === "sending" ? (
                    "Sending..."
                  ) : (
                    <>
                      <UserPlusIcon className="w-4 h-4" />
                      Add as friend
                    </>
                  )}
                </button>
              ) : (
                <Link
                  to={`/login?next=/p/${slug}`}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold bg-indigo-600 hover:bg-indigo-700 text-white shrink-0"
                >
                  <UserPlusIcon className="w-4 h-4" />
                  Sign in to add as friend
                </Link>
              )
            ) : null}
          </div>

          {friendStatus === "error" && friendMessage ? (
            <p className="text-xs text-red-500 mt-3 text-right">{friendMessage}</p>
          ) : null}
        </div>

        {/* RETURN CARD */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
          <div className="p-6 rounded-2xl bg-white/70 dark:bg-[#1a2332]/60 backdrop-blur-xl border border-white/40 dark:border-white/10 shadow-glass dark:shadow-glass-dark">
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-2">
              Total Return
            </p>
            <div className="flex items-center gap-2">
              <TrendIcon
                className={`w-6 h-6 ${
                  positive ? "text-emerald-500" : "text-red-500"
                }`}
              />
              <p
                className={`text-3xl font-extrabold ${
                  positive ? "text-emerald-500" : "text-red-500"
                }`}
              >
                {positive ? "+" : ""}
                {data.total_return_percent.toFixed(2)}%
              </p>
            </div>
            {data.show_amounts && data.total_return != null ? (
              <p className="text-sm text-gray-500 mt-1">
                {formatCurrency(data.total_return, data.currency)}
              </p>
            ) : null}
          </div>

          {data.show_amounts && data.total_value != null ? (
            <div className="p-6 rounded-2xl bg-white/70 dark:bg-[#1a2332]/60 backdrop-blur-xl border border-white/40 dark:border-white/10 shadow-glass dark:shadow-glass-dark">
              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-2">
                Portfolio Value
              </p>
              <p className="text-3xl font-extrabold text-indigo-500">
                {formatCurrency(data.total_value, data.currency)}
              </p>
            </div>
          ) : (
            <div className="p-6 rounded-2xl bg-white/70 dark:bg-[#1a2332]/60 backdrop-blur-xl border border-white/40 dark:border-white/10 shadow-glass dark:shadow-glass-dark">
              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-2">
                Asset classes
              </p>
              <p className="text-3xl font-extrabold text-indigo-500">
                {data.asset_class_allocation?.length || 0}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                Diversification across categories
              </p>
            </div>
          )}
        </div>

        {/* ALLOCATION */}
        {data.asset_class_allocation && data.asset_class_allocation.length > 0 ? (
          <div className="mt-6 p-6 rounded-2xl bg-white/70 dark:bg-[#1a2332]/60 backdrop-blur-xl border border-white/40 dark:border-white/10 shadow-glass dark:shadow-glass-dark">
            <div className="flex items-center gap-2 mb-4">
              <ChartPieIcon className="w-5 h-5 text-indigo-500" />
              <h2 className="text-lg font-bold">Asset allocation</h2>
            </div>
            <div className="space-y-3">
              {data.asset_class_allocation.map((slice) => (
                <Bar key={slice.assetClass} slice={slice} />
              ))}
            </div>
          </div>
        ) : null}

        {/* LATEST BRIEF */}
        {data.brief ? (
          <div className="mt-6 p-6 rounded-2xl bg-white/70 dark:bg-[#1a2332]/60 backdrop-blur-xl border border-amber-500/40 shadow-glass dark:shadow-glass-dark">
            <div className="flex items-center gap-2 mb-2">
              <SparklesIcon className="w-5 h-5 text-amber-500" />
              <span className="text-[10px] font-bold uppercase tracking-widest text-amber-500">
                Latest Compass brief · {new Date(data.brief.created_at).toLocaleDateString()}
              </span>
            </div>
            <h3 className="font-bold text-gray-900 dark:text-white mb-2">
              {data.brief.title}
            </h3>
            {data.brief.first_bullet ? (
              <p className="text-sm text-gray-700 dark:text-gray-300">
                • {data.brief.first_bullet}
              </p>
            ) : null}
          </div>
        ) : null}

        {/* CTA */}
        <div className="mt-10 text-center">
          <p className="text-sm text-gray-500 mb-3">
            Want to track your own portfolio with FinAI's Council of agents?
          </p>
          <Link
            to="/signup"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-indigo-500 text-white font-bold hover:opacity-90 transition"
          >
            <SparklesIcon className="w-4 h-4" />
            Get started — free
          </Link>
        </div>

        <p className="text-center text-[10px] text-gray-400 mt-8">
          Snapshot of {data.display_name}'s portfolio · Powered by FinAI
        </p>
      </div>
    </div>
  );
};

export default PublicPortfolio;
